import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { ArrowLeft, TrendingUp, ShoppingCart, ClipboardCheck, FileCheck } from "lucide-react";

const fmtMoney = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
          <div className="font-display font-bold text-2xl mt-1">{value}</div>
        </div>
        <div className="p-2 rounded-md bg-muted"><Icon className="w-5 h-5 text-muted-foreground" /></div>
      </div>
    </Card>
  );
}

export default function AgentOrders() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsResp, allOrders] = await Promise.all([
        api.get("/users/agents"),
        // Paged fetch-all — a busy BDM can exceed the default 200-row page,
        // and the turnover stats above need the complete set.
        (async () => {
          const PAGE = 500;
          let offset = 0;
          let all = [];
          for (let i = 0; i < 40; i++) {
            const { data } = await api.get("/orders", { params: { agent_id: userId, limit: PAGE, offset } });
            all = all.concat(data || []);
            if (!data || data.length < PAGE) break;
            offset += PAGE;
          }
          return all;
        })(),
      ]);
      const found = (agentsResp.data || []).find((a) => a.id === userId);
      setAgent(found || null);
      // Only show manual orders + approved claimed orders.
      // Approved claims auto-create an order with order_source "Claimed Order",
      // so filtering for these two sources gives the agent's full revenue table.
      const filtered = allOrders.filter(
        (o) => o.order_source === "Manual Order" || o.order_source === "Claimed Order",
      );
      setOrders(filtered);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const manual = orders.filter((o) => o.order_source === "Manual Order");
    const claimed = orders.filter((o) => o.order_source === "Claimed Order");
    const turnover = orders.reduce(
      (sum, o) => sum + Number(o.total_order_amount || 0), 0,
    );
    return {
      manual_count: manual.length,
      claimed_count: claimed.length,
      total_count: orders.length,
      turnover,
    };
  }, [orders]);

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/users")} data-testid="agent-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight" data-testid="agent-title">
            {agent ? agent.name : "Sales BDM"}
          </h1>
          {agent && <p className="text-sm text-muted-foreground">{agent.email}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="agent-stat-total" label="All Orders" value={stats.total_count} icon={ShoppingCart} />
        <StatCard testid="agent-stat-manual" label="Manual Orders" value={stats.manual_count} icon={ClipboardCheck} />
        <StatCard testid="agent-stat-claimed" label="Approved Claim Orders" value={stats.claimed_count} icon={FileCheck} />
        <StatCard testid="agent-stat-turnover" label="Total Turnover" value={fmtMoney(stats.turnover)} icon={TrendingUp} />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-20 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">Order ID</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Fulfillment</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No orders for this BDM</TableCell></TableRow>
              )}
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`agent-order-row-${o.order_id}`}>
                  <TableCell className="font-mono text-xs sticky left-0 z-10 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">{o.order_id}</TableCell>
                  <TableCell><StatusBadge status={o.order_source} /></TableCell>
                  <TableCell className="font-medium text-xs">{o.customer_name}</TableCell>
                  <TableCell className="text-xs">{o.phone}</TableCell>
                  <TableCell className="text-xs max-w-[260px] whitespace-normal break-words">
                    {(o.items || []).map((i) => `${i.sku_name} x${i.quantity}`).join(", ") || "—"}
                  </TableCell>
                  <TableCell><StatusBadge status={o.order_type} /></TableCell>
                  <TableCell className="text-xs whitespace-nowrap font-semibold">{fmtMoney(o.total_order_amount)}</TableCell>
                  <TableCell><StatusBadge status={o.fulfillment_status} /></TableCell>
                  <TableCell><StatusBadge status={o.delivery_status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
